import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Layout from './Layout';
import { useAuth } from '../App';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import {
  LogIn, LogOut, Clock, CalendarDays, CalendarRange, Calendar, History,
  CheckCircle2, XCircle, Loader2, Search, Filter, ChevronUp, ChevronDown,
  RefreshCw, Send, Mail, Shield, BarChart3, Eye, X, MessageCircle, Upload, FileText,
  Unlock, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../utils/config';
import GuestVerificationModal from './GuestVerificationModal';
import QuesturaTimer from './QuesturaTimer';
import { SuccessPopup } from './SuccessPopup';

// Calcola colore riga basato su stato Questura (per CheckinManager)
const getItalyOffsetCM = (date) => {
  const y = date.getFullYear();
  const ml = new Date(Date.UTC(y, 2, 31)); while (ml.getUTCDay() !== 0) ml.setUTCDate(ml.getUTCDate() - 1); ml.setUTCHours(1,0,0,0);
  const ol = new Date(Date.UTC(y, 9, 31)); while (ol.getUTCDay() !== 0) ol.setUTCDate(ol.getUTCDate() - 1); ol.setUTCHours(1,0,0,0);
  return (date >= ml && date < ol) ? 120 : 60;
};

const getQuesturaRowStyle = (b) => {
  if (b.questura_status === 'inviato') return { border: 'border-l-4 border-l-green-500', bg: 'bg-green-50/50' };
  if (b.questura_status === 'ricevuta_manuale') return { border: 'border-l-4 border-l-green-500', bg: 'bg-green-50/50' };
  if (!b.check_in) return { border: '', bg: '' };
  try {
    const ds = b.check_in.includes('T') ? b.check_in.split('T')[0] : b.check_in;
    const [yr,mo,dy] = ds.split('-').map(Number);
    const ci = new Date(Date.UTC(yr,mo-1,dy,14,0)); const o1 = getItalyOffsetCM(ci);
    const ciT = new Date(ci.getTime() - o1*60000);
    const dd = new Date(yr,mo-1,dy+1);
    const dlU = new Date(Date.UTC(dd.getFullYear(),dd.getMonth(),dd.getDate(),23,59));
    const o2 = getItalyOffsetCM(dlU); const dlT = new Date(dlU.getTime()-o2*60000);
    const now = new Date();
    if (now < ciT) return { border: '', bg: '' };
    const h = (dlT-now)/(3600000);
    if (h <= 0) return { border: 'border-l-4 border-l-red-500', bg: 'bg-red-50/60' };
    if (h <= 3) return { border: 'border-l-4 border-l-red-500', bg: 'bg-red-50/40' };
    return { border: 'border-l-4 border-l-orange-400', bg: 'bg-orange-50/40' };
  } catch { return { border: '', bg: '' }; }
};

const CheckinManager = () => {
  const { user } = useAuth();
  const isAdminOrCs = user?.role === 'admin' || user?.role === 'customer_service';
  const [activeView, setActiveView] = useState('arrivals');
  const [period, setPeriod] = useState('today');
  const [bookings, setBookings] = useState([]);
  const [togglingTurnover, setTogglingTurnover] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sortField, setSortField] = useState('check_in');
  const [sortDir, setSortDir] = useState('asc');
  const [filters, setFilters] = useState({
    property: 'all', questura: 'all', docs: 'all', cancelled: 'hide',
    province: 'all', region: 'all'
  });
  const [filterOptions, setFilterOptions] = useState({ properties: [], provinces: [], regions: [] });
  const [verificationBooking, setVerificationBooking] = useState(null);
  const [sendingQuestura, setSendingQuestura] = useState(null);
  const [resendingLink, setResendingLink] = useState(null);
  const [questuraPopup, setQuesturaPopup] = useState({ open: false, title: '', message: '', type: 'success' });
  const [questuraConfirm, setQuesturaConfirm] = useState(null);

  useEffect(() => { fetchData(); }, [period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hw_token');
      const res = await axios.get(`${API}/api/checkin/upcoming?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data || [];
      setBookings(data);
      setFilterOptions({
        properties: [...new Set(data.map(b => b.property_name).filter(Boolean))].sort(),
        provinces: [...new Set(data.map(b => b.property_province).filter(Boolean))].sort(),
        regions: [...new Set(data.map(b => b.property_region).filter(Boolean))].sort(),
      });
    } catch (err) {
      console.error(err);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const [confirmAction, setConfirmAction] = useState(null); // {type, booking, channel}

  const handleResendLink = async (booking) => {
    // Nessun blocco lato client: lo staff d'ufficio puo' reinviare quante volte serve
    // (es. ospite che ha cambiato numero). Il backend applica eventuali limiti per altri ruoli.
    setConfirmAction({ type: 'resend', booking });
  };

  const handleSendReminder = async (booking, channel) => {
    setConfirmAction({ type: 'reminder', booking, channel });
  };

  const handleToggleTurnover = async (booking, enabled) => {
    setTogglingTurnover(booking.booking_id);
    try {
      const token = localStorage.getItem('hw_token');
      await axios.patch(
        `${API}/api/bookings/${booking.booking_id}/guest-turnover`,
        { enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBookings(prev => prev.map(b => b.booking_id === booking.booking_id ? { ...b, guest_turnover_enabled: enabled } : b));
      toast.success(enabled
        ? `Ingresso multiplo ATTIVO per ${booking.guest_name}: ogni ospite avrà date proprie.`
        : `Ingresso multiplo disattivato per ${booking.guest_name}.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Impossibile aggiornare l\'ingresso multiplo');
    } finally {
      setTogglingTurnover(null);
    }
  };

  const executeConfirmedAction = async () => {
    if (!confirmAction) return;
    const { type, booking, channel } = confirmAction;
    setConfirmAction(null);
    
    if (type === 'resend') {
      try {
        setResendingLink(booking.booking_id);
        const token = localStorage.getItem('hw_token');
        const res = await axios.post(`${API}/api/bookings/${booking.booking_id}/resend-checkin-link`, {}, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data.success) {
          const msgs = [];
          if (res.data.email_sent) msgs.push('Email');
          if (res.data.whatsapp_sent) msgs.push('WhatsApp');
          const dest = res.data.sent_to_phone || res.data.sent_to_email || '';
          const channels = msgs.length ? msgs.join(' e ') : 'nessun canale';
          toast.success(`Link reinviato via ${channels}${dest ? ` a ${dest}` : ''}!`);
          setBookings(prev => prev.map(b => b.booking_id === booking.booking_id ? { ...b, checkin_link_resent: true, checkin_link_resent_count: res.data.resent_count } : b));
        } else {
          toast.error('Invio link fallito. Riprova.');
        }
      } catch (err) { toast.error(err.response?.data?.detail || 'Errore reinvio link'); }
      finally { setResendingLink(null); }
    } else if (type === 'reminder') {
      try {
        const token = localStorage.getItem('hw_token');
        const res = await axios.post(`${API}/api/bookings/${booking.booking_id}/send-reminder?channel=${channel}`, {}, { headers: { Authorization: `Bearer ${token}` } });
        if (channel === 'whatsapp' && res.data.whatsapp_link) {
          window.open(res.data.whatsapp_link, '_blank');
          toast.success(`WhatsApp aperto per ${booking.guest_name}`);
        } else if (res.data.success) {
          toast.success(`Email reminder inviato con successo a ${booking.guest_name}!`);
        } else {
          toast.error('Invio fallito. Verifica che l\'ospite abbia un contatto valido.');
        }
      } catch (err) { toast.error(err.response?.data?.detail || 'Errore invio reminder'); }
    }
  };

  const handleResetManualFlags = async (booking) => {
    if (!isAdminOrCs) return;
    if (!window.confirm(`Vuoi resettare lo stato manuale Questura per ${booking.guest_name}?\n\nQuesto cancella i flag "marcato manualmente" / "inviato manualmente". La ricevuta caricata (se presente) NON viene rimossa.`)) {
      return;
    }
    try {
      const token = localStorage.getItem('hw_token');
      const r = await fetch(`${API}/api/bookings/${booking.booking_id}/admin-reset-questura-flags`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Errore reset');
        return;
      }
      const data = await r.json();
      toast.success(data.message || 'Flag manuali resettati');
      fetchData();
    } catch (e) {
      toast.error('Errore di rete');
    }
  };

  const handleQuesturaConfirm = async (booking) => {
    // Blocca se già inviato (sempre)
    if (booking.questura_status === 'inviato') {
      toast.info('Questa prenotazione è già stata inviata alla Questura.');
      return;
    }
    // Se marcata manualmente: blocca solo i non-admin. Admin/CS può comunque procedere.
    if (booking.questura_manual_marked && !isAdminOrCs) {
      toast.info('Questa prenotazione è stata marcata come inviata manualmente. Carica la ricevuta.');
      return;
    }
    if (!booking.docs_complete) {
      if (booking.docs_total === 0) {
        toast.error(`Nessun documento inserito per ${booking.guest_name}. Inserisci i documenti di tutti i ${booking.guests_count} ospiti prima di inviare.`, { duration: 6000 });
      } else if (booking.docs_total > booking.guests_count) {
        toast.error(`Ci sono ${booking.docs_total} documenti per ${booking.guests_count} ospiti. Elimina i documenti in eccesso dalla scheda verifica.`, { duration: 6000 });
      } else {
        toast.error(`Documenti incompleti: ${booking.docs_verified}/${booking.guests_count}. Apri la scheda verifica e completa i documenti mancanti.`, { duration: 6000 });
      }
      return;
    }
    if (booking.docs_total > booking.guests_count) {
      toast.error(`Ci sono ${booking.docs_total} documenti per ${booking.guests_count} ospiti. Elimina i documenti in eccesso dalla scheda verifica prima di inviare alla Questura.`, { duration: 6000 });
      return;
    }

    // Pre-validazione dati ospiti prima di conferma
    try {
      const token = localStorage.getItem('hw_token');
      const valResponse = await axios.get(
        `${API}/api/alloggiati/validate/${booking.booking_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const valData = valResponse.data;
      if (valData.errors && valData.errors.length > 0) {
        const criticalErrors = valData.errors.filter(e =>
          e.error.toLowerCase().includes('obbligatorio') ||
          e.error.toLowerCase().includes('non riconosciuto') ||
          e.error.toLowerCase().includes('errato')
        );
        if (criticalErrors.length > 0) {
          const errorDetails = valData.errors.map(e => `${e.guest}: ${e.field} - ${e.error}`);
          setQuesturaPopup({
            open: true,
            title: 'Errori nei dati ospiti',
            message: `Trovati ${criticalErrors.length} errori che impediscono l'invio alla Questura. Correggi i dati nella scheda verifica e riprova.`,
            type: 'error',
            details: errorDetails,
          });
          return;
        }
        if (valData.errors.length > 0) {
          toast.warning(`Attenzione: ${valData.errors.length} avvisi sui dati ospiti. Verifica prima dell'invio.`);
        }
      }
    } catch (valErr) {
      console.warn('Validation check failed, proceeding anyway:', valErr);
    }

    setQuesturaConfirm(booking);
  };

  const handleSendQuestura = async (booking) => {
    setQuesturaConfirm(null);
    try {
      setSendingQuestura(booking.booking_id);
      const token = localStorage.getItem('hw_token');
      const res = await axios.post(`${API}/api/alloggiati/submit/${booking.booking_id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) {
        setQuesturaPopup({ open: true, title: 'Invio avvenuto!', message: `Dati per ${booking.guest_name} inviati alla Questura.`, type: 'success', details: [] });
        fetchData();
      } else {
        const errs = [];
        if (res.data.validation_errors) errs.push(...res.data.validation_errors);
        if (res.data.errors) res.data.errors.forEach(e => errs.push(typeof e === 'string' ? e : e.description || e.message || 'Errore'));
        setQuesturaPopup({ open: true, title: 'Errore Questura', message: res.data.error || 'Errore invio', type: 'error', details: errs });
      }
    } catch (err) {
      const d = err.response?.data;
      const errs = [];
      if (d?.validation_errors) errs.push(...d.validation_errors);
      let msg = d?.error || d?.detail || 'Errore connessione';
      // Messaggi specifici e azionabili
      if (msg.includes('GestioneAppartamenti') || msg.includes('metodi appropriati')) {
        msg = "L'account Questura è di tipo 'Gestore Appartamenti'. Configura l'ID Appartamento nella scheda immobile (Configurazione Immobili > Configura).";
      } else if (msg.includes('ERRORE_PARAMETRO_ERRATO')) {
        msg = 'Errore parametro: verifica che tutti i campi ospite siano compilati correttamente nella scheda verifica.';
      } else if (msg.includes('credenziali') || msg.includes('wskey') || msg.includes('401')) {
        msg = 'Credenziali Questura non valide. Aggiorna la wskey nella configurazione immobile.';
      } else if (msg.includes('timeout') || msg.includes('connection')) {
        msg = 'Il server della Questura non risponde. Riprova tra qualche minuto.';
      }
      setQuesturaPopup({ open: true, title: 'Errore Questura', message: msg, type: 'error', details: errs });
    } finally { setSendingQuestura(null); }
  };

  const rawData = useMemo(() => {
    return bookings.filter(b => activeView === 'arrivals' ? b.is_arrival : b.is_departure);
  }, [bookings, activeView]);

  const filtered = useMemo(() => {
    let result = [...rawData];
    if (filters.cancelled === 'hide') result = result.filter(b => !b.cancelled);
    else if (filters.cancelled === 'only') result = result.filter(b => b.cancelled);
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      result = result.filter(b =>
        b.guest_name?.toLowerCase().includes(s) || b.guest_email?.toLowerCase().includes(s) ||
        b.guest_phone?.includes(s) || b.property_name?.toLowerCase().includes(s) ||
        b.booking_code?.toLowerCase().includes(s) || b.property_city?.toLowerCase().includes(s)
      );
    }
    if (filters.property !== 'all') result = result.filter(b => b.property_name === filters.property);
    if (filters.province !== 'all') result = result.filter(b => b.property_province === filters.province);
    if (filters.region !== 'all') result = result.filter(b => b.property_region === filters.region);
    if (filters.questura !== 'all') result = result.filter(b => b.questura_status === filters.questura);
    if (filters.docs !== 'all') {
      if (filters.docs === 'complete') result = result.filter(b => b.docs_complete);
      else if (filters.docs === 'incomplete') result = result.filter(b => !b.docs_complete && b.docs_total > 0);
      else if (filters.docs === 'none') result = result.filter(b => b.docs_total === 0);
    }
    result.sort((a, b) => {
      let av = a[sortField] ?? '', bv = b[sortField] ?? '';
      if (typeof av === 'string') { av = av.toLowerCase(); bv = bv.toLowerCase(); }
      return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
    return result;
  }, [rawData, searchTerm, filters, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronUp className="h-3 w-3 text-gray-300" />;
    return sortDir === 'asc' ? <ChevronUp className="h-3 w-3 text-blue-600" /> : <ChevronDown className="h-3 w-3 text-blue-600" />;
  };

  const clearFilters = () => {
    setFilters({ property: 'all', questura: 'all', docs: 'all', cancelled: 'hide', province: 'all', region: 'all' });
    setSearchTerm('');
  };

  const activeCount = Object.entries(filters).filter(([k, v]) => k === 'cancelled' ? v !== 'hide' : v !== 'all').length + (searchTerm ? 1 : 0);

  const arrivalsCount = bookings.filter(b => b.is_arrival && !b.cancelled).length;
  const departuresCount = bookings.filter(b => b.is_departure && !b.cancelled).length;

  const getNights = (ci, co) => {
    if (!ci || !co) return '-';
    return Math.max(1, Math.ceil((new Date(co) - new Date(ci)) / 86400000));
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }) : '-';

  const periods = [
    { id: 'last48h', label: '48h', icon: History, color: 'purple' },
    { id: 'today', label: 'Oggi', icon: Clock, color: 'blue' },
    { id: 'week', label: 'Settimana', icon: CalendarDays, color: 'blue' },
    { id: 'month', label: 'Mese', icon: CalendarRange, color: 'blue' },
    { id: 'year', label: 'Anno', icon: Calendar, color: 'indigo' },
  ];

  return (
    <Layout>
      <div className="space-y-4" data-testid="checkin-manager">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-800">Gestione Check-in</h1>
            <p className="text-sm text-slate-500">{filtered.length} prenotazioni nel periodo</p>
          </div>
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="period-filter">
            {periods.map(p => (
              <Button key={p.id} variant={period === p.id ? 'default' : 'ghost'} size="sm"
                onClick={() => setPeriod(p.id)}
                className={`whitespace-nowrap text-xs ${period === p.id ? `bg-${p.color}-600 text-white` : ''}`}>
                <p.icon className="h-3 w-3 mr-1" /> {p.label}
              </Button>
            ))}
            <Button variant="ghost" size="sm" onClick={fetchData} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Arrivi / Partenze Toggle */}
        <div className="flex gap-3" data-testid="view-toggle">
          <Button onClick={() => setActiveView('arrivals')}
            className={`flex-1 h-14 text-lg ${activeView === 'arrivals' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-white border-2 border-green-600 text-green-600 hover:bg-green-50'}`}
            data-testid="btn-arrivals">
            <LogIn className="h-5 w-5 mr-2" /> Arrivi ({arrivalsCount})
          </Button>
          <Button onClick={() => setActiveView('departures')}
            className={`flex-1 h-14 text-lg ${activeView === 'departures' ? 'bg-orange-600 hover:bg-orange-700 text-white' : 'bg-white border-2 border-orange-600 text-orange-600 hover:bg-orange-50'}`}
            data-testid="btn-departures">
            <LogOut className="h-5 w-5 mr-2" /> Partenze ({departuresCount})
          </Button>
        </div>

        {/* Filters */}
        <Card className="p-4" data-testid="filters-section">
          <div className="flex items-center justify-between mb-3">
            <Button variant="ghost" size="sm" onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn">
              <Filter className="h-4 w-4 mr-1" />
              {showFilters ? 'Nascondi Filtri' : 'Mostra Filtri'}
              {activeCount > 0 && <Badge className="ml-2 bg-blue-100 text-blue-700">{activeCount}</Badge>}
            </Button>
            {activeCount > 0 && <Button variant="outline" size="sm" onClick={clearFilters} data-testid="clear-filters-btn"><X className="h-3 w-3 mr-1" /> Reset</Button>}
          </div>
          {showFilters && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              <div className="relative col-span-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input placeholder="Cerca nome, email, telefono, immobile, codice..." value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-input" />
              </div>
              <FSelect label="Immobile" value={filters.property} onChange={v => setFilters(f => ({...f, property: v}))}
                options={filterOptions.properties} testId="filter-property" />
              <FSelect label="Provincia" value={filters.province} onChange={v => setFilters(f => ({...f, province: v}))}
                options={filterOptions.provinces} testId="filter-province" />
              <FSelect label="Regione" value={filters.region} onChange={v => setFilters(f => ({...f, region: v}))}
                options={filterOptions.regions} testId="filter-region" />
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Documenti</label>
                <Select value={filters.docs} onValueChange={v => setFilters(f => ({...f, docs: v}))}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    <SelectItem value="complete">Completi</SelectItem>
                    <SelectItem value="incomplete">Incompleti</SelectItem>
                    <SelectItem value="none">Nessuno</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Questura</label>
                <Select value={filters.questura} onValueChange={v => setFilters(f => ({...f, questura: v}))}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    <SelectItem value="inviato">Inviato (auto)</SelectItem>
                    <SelectItem value="ricevuta_manuale">Ricevuta manuale</SelectItem>
                    <SelectItem value="in_attesa_ricevuta">In attesa ricevuta</SelectItem>
                    <SelectItem value="parziale">Parziale</SelectItem>
                    <SelectItem value="non_inviato">Non inviato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </Card>

        {/* Legend + Summary Badges */}
        <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-slate-600 bg-slate-50 rounded-lg p-2">
          <span className="font-medium">Legenda:</span>
          <span className="flex items-center gap-1"><CheckCircle2 className="h-4 w-4 text-green-500" /> Completo</span>
          <span className="flex items-center gap-1"><CheckCircle2 className="h-4 w-4 text-amber-500" /> Parziale</span>
          <span className="flex items-center gap-1"><XCircle className="h-4 w-4 text-red-500" /> Mancante</span>
          <span className="mx-1 text-slate-300">|</span>
          <span className="flex items-center gap-1"><Clock className="h-4 w-4 text-orange-500" /> Attesa ricevuta</span>
          <span className="flex items-center gap-1"><FileText className="h-4 w-4 text-blue-500" /> Ricevuta manuale</span>
          {(() => {
            const noDocsNoQuestura = filtered.filter(b => b.docs_total === 0 && !b.has_questura).length;
            const waitingReceipt = filtered.filter(b => b.questura_status === 'in_attesa_ricevuta').length;
            return noDocsNoQuestura > 0 || waitingReceipt > 0 ? (
              <>
                <span className="mx-1 text-slate-300">|</span>
                {noDocsNoQuestura > 0 && (
                  <button 
                    className="flex items-center gap-1 bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium hover:bg-red-200 transition-colors"
                    onClick={() => { setFilters(f => ({...f, docs: 'none', questura: 'in_attesa_ricevuta'})); setShowFilters(true); }}
                    data-testid="badge-no-docs-no-questura"
                  >
                    <XCircle className="h-3.5 w-3.5" /> {noDocsNoQuestura} senza docs + senza Questura
                  </button>
                )}
                {waitingReceipt > 0 && (
                  <button 
                    className="flex items-center gap-1 bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium hover:bg-orange-200 transition-colors"
                    onClick={() => { setFilters(f => ({...f, questura: 'in_attesa_ricevuta'})); setShowFilters(true); }}
                    data-testid="badge-waiting-receipt"
                  >
                    <Clock className="h-3.5 w-3.5" /> {waitingReceipt} in attesa ricevuta
                  </button>
                )}
              </>
            ) : null;
          })()}
        </div>

        {/* Table */}
        <Card className="overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              <span className="ml-3 text-slate-600">Caricamento...</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-500">
              <Calendar className="h-16 w-16 text-slate-300 mb-4" />
              <p className="text-lg font-medium">Nessun {activeView === 'arrivals' ? 'arrivo' : 'partenza'} nel periodo</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="checkin-table">
                <thead className="bg-gray-50 border-y">
                  <tr>
                    {[
                      { f: 'check_in', l: 'Arrivo' }, { f: 'check_out', l: 'Partenza' },
                      { f: 'guest_name', l: 'Ospite' }, { f: 'property_name', l: 'Immobile' },
                      { f: 'guests_count', l: 'Ospiti' },
                    ].map(c => (
                      <th key={c.f} onClick={() => handleSort(c.f)}
                        className="px-3 py-3 text-left font-medium text-gray-600 cursor-pointer hover:bg-gray-100 whitespace-nowrap">
                        <div className="flex items-center gap-1">{c.l} <SortIcon field={c.f} /></div>
                      </th>
                    ))}
                    <th className="px-3 py-3 text-center font-medium text-gray-600">Notti</th>
                    <th className="px-3 py-3 text-center font-medium text-gray-600">Pre Check-in</th>
                    <th className="px-3 py-3 text-center font-medium text-gray-600">Questura</th>
                    <th className="px-3 py-3 text-center font-medium text-gray-600">ISTAT</th>
                    <th className="px-3 py-3 text-center font-medium text-gray-600">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filtered.map((b, i) => {
                    const nights = getNights(b.check_in, b.check_out);
                    const isToday = b.check_in === new Date().toISOString().split('T')[0] || b.check_out === new Date().toISOString().split('T')[0];
                    const qRow = getQuesturaRowStyle(b);
                    return (
                      <tr key={b.booking_id || i}
                        className={`hover:bg-gray-50/80 ${qRow.border} ${b.cancelled ? 'bg-red-50 opacity-60' : qRow.bg || (isToday ? 'bg-yellow-50' : '')}`}
                        data-testid={`row-${b.booking_id || i}`}>
                        <td className="px-3 py-2.5 whitespace-nowrap text-xs font-medium">{formatDate(b.check_in)}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-xs">{formatDate(b.check_out)}</td>
                        <td className="px-3 py-2.5">
                          <div className={b.cancelled ? 'line-through text-red-600' : ''}>
                            <p className="font-medium text-sm">{b.guest_name || 'N/A'}</p>
                            {b.guest_email && <p className="text-xs text-gray-500 truncate max-w-[180px]">{b.guest_email}</p>}
                            {b.guest_phone && <p className="text-xs text-gray-400">{b.guest_phone}</p>}
                          </div>
                          {b.cancelled && <Badge className="bg-red-600 text-white text-[10px] mt-1">ANNULLATA</Badge>}
                          {!b.cancelled && (
                            <div className="mt-1 flex items-center gap-1.5" data-testid={`turnover-row-${b.booking_id}`}>
                              <Checkbox
                                id={`turnover-${b.booking_id}`}
                                checked={!!b.guest_turnover_enabled}
                                disabled={togglingTurnover === b.booking_id}
                                onCheckedChange={(v) => handleToggleTurnover(b, !!v)}
                                className="h-3.5 w-3.5 border-amber-400 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                                data-testid={`turnover-checkbox-${b.booking_id}`}
                              />
                              <label
                                htmlFor={`turnover-${b.booking_id}`}
                                className="text-[10px] text-amber-700 cursor-pointer leading-tight select-none"
                                title="Attiva se gli ospiti entrano/escono in date diverse durante il soggiorno (avvicendamento)"
                              >
                                {togglingTurnover === b.booking_id ? 'Salvataggio…' : 'Ingresso multiplo'}
                              </label>
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <p className="text-xs font-medium notranslate truncate max-w-[140px]" translate="no">{b.property_name}</p>
                          <p className="text-[10px] text-gray-400">{b.property_city}{b.property_province ? ` (${b.property_province})` : ''}</p>
                        </td>
                        <td className="px-3 py-2.5 text-center text-xs">{b.guests_count}</td>
                        <td className="px-3 py-2.5 text-center text-xs">{nights}</td>
                        {/* Pre Check-in */}
                        <td className="px-3 py-2.5 text-center">
                          {b.cancelled ? <span className="text-gray-300">-</span> : (
                            <div className="flex items-center justify-center gap-1">
                              {b.docs_complete ? (
                                <CheckCircle2 className="h-5 w-5 text-green-500" />
                              ) : b.docs_total > 0 ? (
                                <CheckCircle2 className="h-5 w-5 text-amber-500" />
                              ) : (
                                <XCircle className="h-5 w-5 text-red-500" />
                              )}
                              <span className="text-[10px] text-gray-500">{b.docs_verified}/{b.guests_count}</span>
                            </div>
                          )}
                        </td>
                        {/* Questura */}
                        <td className="px-3 py-2.5 text-center">
                          {b.cancelled ? <span className="text-gray-300">-</span> : b.questura_status === 'inviato' ? (
                            <div className="flex items-center justify-center gap-1" title={`Inviato${b.questura_manual_sent_by ? ` manualmente da ${b.questura_manual_sent_by}` : ' automaticamente'}${b.questura_sent_at ? ` (${new Date(b.questura_sent_at).toLocaleString('it-IT')})` : ''}`}>
                              <CheckCircle2 className="h-5 w-5 text-green-500 inline" />
                              {b.questura_manual_sent && (
                                <span className="text-[9px] text-green-700">Manuale</span>
                              )}
                              {isAdminOrCs && b.questura_manual_sent && (
                                <button
                                  onClick={() => handleResetManualFlags(b)}
                                  className="ml-1 text-[9px] text-orange-600 hover:text-orange-800 underline"
                                  title="Admin: sblocca flag manuali per re-inviare"
                                  data-testid={`admin-reset-manual-${i}`}
                                >
                                  Sblocca
                                </button>
                              )}
                            </div>
                          ) : b.questura_status === 'parziale' ? (
                            <CheckCircle2 className="h-5 w-5 text-amber-500 inline" />
                          ) : b.questura_status === 'ricevuta_manuale' ? (
                            <div className="flex items-center justify-center gap-1" title={`Ricevuta caricata: ${b.questura_manual_receipt_file || ''}`}>
                              <FileText className="h-5 w-5 text-blue-500 inline" />
                              <span className="text-[9px] text-blue-600">Manuale</span>
                            </div>
                          ) : b.questura_manual_marked ? (
                            <div className="flex flex-col items-center justify-center gap-0.5" title={`Marcato come inviato a mano${b.questura_manual_marked_by ? ` da ${b.questura_manual_marked_by}` : ''}${b.questura_manual_marked_at ? ` il ${new Date(b.questura_manual_marked_at).toLocaleString('it-IT')}` : ''} - in attesa ricevuta`}>
                              <div className="flex items-center justify-center gap-1">
                                <AlertTriangle className="h-4 w-4 text-orange-500 inline" />
                                <span className="text-[9px] text-orange-700">Marcato a mano</span>
                              </div>
                              {isAdminOrCs && (
                                <button
                                  onClick={() => handleResetManualFlags(b)}
                                  className="text-[9px] text-orange-700 hover:text-orange-900 underline flex items-center gap-0.5"
                                  title="Admin: resetta il flag manuale per riprendere il controllo"
                                  data-testid={`admin-reset-marked-${i}`}
                                >
                                  <Unlock className="h-3 w-3" />Sblocca
                                </button>
                              )}
                            </div>
                          ) : b.questura_status === 'in_attesa_ricevuta' ? (
                            <div className="flex items-center justify-center gap-1" title="Immobile senza Questura automatica - in attesa ricevuta dal responsabile">
                              <Clock className="h-5 w-5 text-orange-500 inline" />
                              <span className="text-[9px] text-orange-600">Attesa</span>
                            </div>
                          ) : !b.has_questura ? (
                            <Badge variant="outline" className="text-[10px] bg-gray-50">N/C</Badge>
                          ) : (
                            <button
                              className="inline-flex items-center gap-1 text-[10px] text-red-600 hover:text-green-600 hover:bg-green-50 px-1.5 py-0.5 rounded transition-colors"
                              title="Segna come inviato manualmente"
                              onClick={async () => {
                                try {
                                  const res = await fetch(`${API}/api/bookings/${b.booking_id}/mark-questura-sent`, {
                                    method: 'PATCH',
                                    headers: { Authorization: `Bearer ${localStorage.getItem('hw_token')}` }
                                  });
                                  if (res.ok) {
                                    toast.success('Marcato come inviato manualmente');
                                    fetchData();
                                  }
                                } catch { toast.error('Errore'); }
                              }}
                              data-testid={`mark-questura-${i}`}
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                        {/* ISTAT */}
                        <td className="px-3 py-2.5 text-center">
                          {b.cancelled ? <span className="text-gray-300">-</span> : !b.has_istat ? (
                            <Badge variant="outline" className="text-[10px] bg-gray-50">N/C</Badge>
                          ) : b.istat_status === 'inviato' ? (
                            <CheckCircle2 className="h-5 w-5 text-green-500 inline" />
                          ) : (
                            <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Da inviare</Badge>
                          )}
                        </td>
                        {/* Azioni */}
                        <td className="px-3 py-2.5">
                          {!b.cancelled && (
                            <div className="flex items-center justify-center gap-1 flex-wrap">
                              {!b.docs_complete && (
                                <Button variant="outline" size="sm" className="h-7 px-2 text-[10px]"
                                  onClick={() => setVerificationBooking({ id: b.booking_id, guest_name: b.guest_name, guests_count: b.guests_count, check_in: b.check_in, check_out: b.check_out, property_id: b.property_id })}
                                  data-testid={`verify-btn-${i}`}>
                                  <Eye className="h-3 w-3 mr-0.5" /> Verifica
                                </Button>
                              )}
                              {!b.docs_complete && (
                                <Button variant="outline" size="sm" className="h-7 px-2 text-[10px]"
                                  onClick={() => handleResendLink(b)} disabled={resendingLink === b.booking_id}
                                  title="Invia/Reinvia il link pre-check-in via Email + WhatsApp al contatto attuale"
                                  data-testid={`resend-btn-${i}`}>
                                  {resendingLink === b.booking_id
                                    ? <Loader2 className="h-3 w-3 mr-0.5 animate-spin" />
                                    : <Send className="h-3 w-3 mr-0.5" />}
                                  {b.has_token ? 'Reinvia' : 'Link'}
                                  {b.checkin_link_resent_count > 0 && <span className="ml-1 text-amber-600">({b.checkin_link_resent_count})</span>}
                                </Button>
                              )}
                              {b.has_token && !b.docs_complete && (
                                <>
                                  <Button variant="outline" size="sm" className="h-7 px-2 text-[10px]"
                                    onClick={() => handleSendReminder(b, 'email')} data-testid={`email-btn-${i}`}>
                                    <Mail className="h-3 w-3" />
                                  </Button>
                                  <Button variant="outline" size="sm" className="h-7 px-2 text-[10px]"
                                    onClick={() => handleSendReminder(b, 'whatsapp')} data-testid={`wa-btn-${i}`}>
                                    <MessageCircle className="h-3 w-3" />
                                  </Button>
                                </>
                              )}
                              {b.has_questura && b.docs_complete && b.questura_status !== 'inviato' && (!b.questura_manual_marked || isAdminOrCs) && (
                                <Button size="sm" className={`h-7 px-2 text-[10px] ${b.questura_manual_marked ? 'bg-orange-600 hover:bg-orange-700' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
                                  onClick={() => handleQuesturaConfirm(b)} disabled={sendingQuestura === b.booking_id}
                                  title={b.questura_manual_marked ? 'Forza invio Questura (override flag manuale)' : 'Invia alla Questura'}
                                  data-testid={`questura-btn-${i}`}>
                                  {sendingQuestura === b.booking_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Shield className="h-3 w-3 mr-0.5" />}
                                  {b.questura_manual_marked ? 'Forza Q' : 'Q'}
                                </Button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Questura Confirmation Modal */}
      {questuraConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setQuesturaConfirm(null)}>
          <Card className="w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-2">Conferma Invio Questura</h3>
            <p className="text-sm text-gray-600 mb-4">
              Vuoi inviare i dati di <strong>{questuraConfirm.guest_name}</strong> ({questuraConfirm.guests_count} ospiti) alla Questura?
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setQuesturaConfirm(null)}>Annulla</Button>
              <Button className="bg-blue-600 text-white" onClick={() => handleSendQuestura(questuraConfirm)}>Invia</Button>
            </div>
          </Card>
        </div>
      )}

      {/* Questura Result Popup */}
      {questuraPopup.open && (
        <SuccessPopup
          open={questuraPopup.open}
          onClose={() => setQuesturaPopup(p => ({ ...p, open: false }))}
          title={questuraPopup.title}
          message={questuraPopup.message}
          type={questuraPopup.type}
          details={questuraPopup.details || []}
        />
      )}

      {/* Guest Verification Modal */}
      {verificationBooking && (
        <GuestVerificationModal
          booking={verificationBooking}
          allowTurnoverToggle={true}
          onClose={() => { setVerificationBooking(null); fetchData(); }}
        />
      )}

      {/* Conferma Azione Dialog */}
      <AlertDialog open={!!confirmAction} onOpenChange={() => setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === 'resend' ? 'Reinvio Link Pre Check-in' :
               confirmAction?.channel === 'whatsapp' ? 'Invio WhatsApp' : 'Invio Email Reminder'}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                {confirmAction?.type === 'resend' 
                  ? `Vuoi reinviare il link di pre check-in a ${confirmAction?.booking?.guest_name}?`
                  : confirmAction?.channel === 'whatsapp'
                    ? `Si aprirà WhatsApp per inviare il promemoria documenti a ${confirmAction?.booking?.guest_name}.`
                    : `Vuoi inviare un promemoria email per i documenti a ${confirmAction?.booking?.guest_name}?`
                }
              </span>
              <span className="block text-xs text-slate-500">
                {confirmAction?.type === 'resend' 
                  ? `Verrà inviato via Email${confirmAction?.booking?.guest_email ? ` (${confirmAction.booking.guest_email})` : ''} e WhatsApp${confirmAction?.booking?.guest_phone ? ` (${confirmAction.booking.guest_phone})` : ''} al contatto attuale.`
                  : confirmAction?.channel === 'whatsapp'
                    ? `Numero: ${confirmAction?.booking?.guest_phone || 'non disponibile'}`
                    : `Email: ${confirmAction?.booking?.guest_email || 'non disponibile'}`
                }
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={executeConfirmedAction} data-testid="confirm-send">
              {confirmAction?.channel === 'whatsapp' ? 'Apri WhatsApp' : 'Invia'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

function FSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-xs text-gray-500 mb-1 block">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-9" data-testid={testId}><SelectValue placeholder="Tutti" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tutti</SelectItem>
          {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export default CheckinManager;
